import BrewbaseIconAnim from "@/components/icons/brewbase-icon-anim";
import { Sidebar } from "@/components/nav/sidebar";
import { MobileNav } from "@/components/nav/mobile-nav";
import { useSidebarStore } from "@/components/nav/sidebar-store";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "@/hooks/query";
import { trpc, trpcClient } from "@/hooks/trpc";
import { supabase } from "@/lib/supabase";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRoute,
  HeadContent,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { TriangleAlertIcon } from "lucide-react";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useHotkeys } from "react-hotkeys-hook";
import { CapLabelDialog } from "@/features/analytics/cap-label-dialog";
import { Toaster } from "sonner";
import { GlobalAlertDialog } from "@/components/global-alert-dialog";
import { ActiveAlertDialog } from "@/features/fermentation-alerts/active-alert-dialog";
import { TemplateCorrectionDialog } from "@/features/template-correction/template-correction-dialog";
import { AnnouncementHost } from "@/features/announcements/announcement-host";
import { GlobalKeyboardShortcutsProvider } from "@/components/global-keyboard-shortcuts";
import { useIsMobile } from "@/hooks/use-mobile";

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    if (
      !location.pathname.startsWith("/login") &&
      !location.pathname.startsWith("/upload") &&
      !location.pathname.startsWith("/sensory") &&
      !location.pathname.startsWith("/consent") &&
      !location.pathname.startsWith("/alert")
    ) {
      if (
        import.meta.env.DEV &&
        import.meta.env.VITE_DEV_AUTH_BYPASS === "true"
      ) {
        return;
      }
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw redirect({ to: "/login", search: { next: location.href } });
      }
    }
  },
  onError(err) {
    console.error(err);
  },
  onCatch(error) {
    console.error(error);
  },
  loader: async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return { session };
  },
  pendingComponent: Loading,
  component: () => (
    <QueryClientProvider client={queryClient}>
      <DefaultLayout />
    </QueryClientProvider>
  ),
});

function DefaultLayout() {
  const focus = useSidebarStore((state) => state.focus);
  const overlayOpen = useSidebarStore((state) => state.overlayOpen);
  const setOverlayOpen = useSidebarStore((state) => state.setOverlayOpen);
  const isMobile = useIsMobile();
  const location = useLocation();
  const isPublicPage =
    location.pathname.startsWith("/upload") ||
    location.pathname.startsWith("/sensory") ||
    location.pathname.startsWith("/consent") ||
    // Opened from an alert text message, on a phone that is not signed in.
    location.pathname.startsWith("/alert") ||
    // A tasting's stage fills a projector: signed in, but no app chrome.
    location.pathname.startsWith("/stage");

  useHotkeys(
    "mod+/",
    (e) => {
      e.preventDefault();
      setOverlayOpen(!overlayOpen);
    },
    { enabled: focus },
  );

  const { session } = Route.useLoaderData();
  const devBypass =
    import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === "true";
  const hasSession = !!session || devBypass;

  if (isPublicPage) {
    return (
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <div className="min-h-screen bg-olive-1">
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
          <Toaster />
        </div>
      </trpc.Provider>
    );
  }

  return (
    <>
      <HeadContent />

      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <TooltipProvider>
          <GlobalKeyboardShortcutsProvider>
            <div className="flex w-screen h-screen overflow-hidden">
              {/* Desktop sidebar */}
              {hasSession && !isMobile && <Sidebar />}

              {/* Main content area */}
              <div data-main-content className="relative flex-grow bg-olive-1 w-full min-w-0 flex flex-col overflow-y-auto">
                <ErrorBoundary
                  onError={(error) => {
                    console.error(error);
                  }}
                  fallbackRender={(p) => (
                    <div className="flex-grow flex flex-col items-center justify-center text-tomato-9">
                      <p className="flex items-center">
                        <TriangleAlertIcon className="w-4 h-4 mr-2" />
                        <span>
                          An unhandled error occurred. The issue has been
                          logged.
                        </span>
                      </p>
                      <p>{p.error.toString()}</p>
                    </div>
                  )}
                >
                  <Suspense fallback={<Loading />}>
                    <Outlet />
                  </Suspense>
                </ErrorBoundary>
              </div>

              {/* Mobile bottom nav */}
              {hasSession && isMobile && <MobileNav />}

              <Toaster />
              <GlobalAlertDialog />
              {hasSession && <CapLabelDialog />}
              {hasSession && <ActiveAlertDialog />}
              {hasSession && <AnnouncementHost />}
              <TemplateCorrectionDialog />
            </div>
          </GlobalKeyboardShortcutsProvider>
        </TooltipProvider>
      </trpc.Provider>
    </>
  );
}

function Loading() {
  return (
    <div className="flex-1 w-full h-full flex items-center justify-center">
      <BrewbaseIconAnim progress={50} />
    </div>
  );
}
